// Shift-wise DCR Closing: at the end of a shift the counter closes its cash. The system adds up what was
// collected in that shift (Protocol tickets issued / Saree sales) by payment mode and by operator; the
// operator enters the opening balance, counts the cash (note-wise) and the UPI / card / other settlement,
// and the shortage / excess and the closing balance are worked out. The Register lists every closing.
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import { istDayKey } from "../lib/istDate";
import { ReportHeader, makePdf } from "./Reports";
import { isCounterUser, templeOptions, useCounterShiftMeta } from "../components/CounterShiftFilter";
import { PAY_MODES } from "../components/PaymentModePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Printer, FileDown, Lock, RotateCcw, Pencil, RefreshCw, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const NOTES = [500, 200, 100, 50, 20, 10, 5, 2, 1];
const TITLES = { PROTOCOL: "Protocol Darshan", SAREES: "Saree Sales" };
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (v) => (v > 0 ? `+${inr(v)}` : v < 0 ? `−${inr(-v)}` : inr(0));
const diffClass = (v) => (v < 0 ? "text-red-700" : v > 0 ? "text-green-700" : "text-slate-700");
const resultLabel = (v) => (v < 0 ? "SHORTAGE" : v > 0 ? "EXCESS" : "TALLY");
const fmtDay = (d) => (d ? d.split("-").reverse().join("-") : "");
const nowIstHm = () => new Date(Date.now() + 330 * 60000).toISOString().slice(11, 16);
const shiftNow = (shifts) => {
  const t = nowIstHm();
  return shifts.find((s) => s.status !== "INACTIVE" && s.start && s.end && (s.start < s.end ? s.start <= t && t < s.end : t >= s.start || t < s.end));
};
const MODE_TONE = { CASH: "bg-[#EAF8EF] text-[#1F7A45]", UPI: "bg-[#EEF4FF] text-[#2F5BC4]", CARD: "bg-[#F4EEFF] text-[#6D35D6]", OTHER: "bg-[#FFF2E8] text-[#C55410]" };

function Th({ children, right }) { return <th className={`py-2 px-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, bold, className = "" }) { return <td className={`py-1.5 px-2.5 ${right ? "text-right" : ""} ${bold ? "font-bold" : ""} ${className}`}>{children}</td>; }

/** Closing statement (screen + A4 print): system collection, operator-wise, item-wise, cash count and the result. */
function Statement({ module, temple, s, c, live, innerRef }) {
  const sys = (c && c.system) || s.system;
  const counterName = c ? c.counter_name : (s.counter ? s.counter.name : "All counters");
  const shift = s.shift;
  const v = c || live;
  return (
    <div ref={innerRef} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-5 text-sm" data-testid="closing-statement">
      <ReportHeader title={`Shift-wise DCR Closing — ${TITLES[module]}`} temple={temple} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mb-4 text-[13px]">
        <p><span className="text-slate-500">Date:</span> <b>{fmtDay(s.date)}</b></p>
        <p><span className="text-slate-500">Shift:</span> <b>{shift.name} ({shift.start}–{shift.end})</b></p>
        <p><span className="text-slate-500">Counter:</span> <b>{counterName}</b></p>
        <p><span className="text-slate-500">{module === "PROTOCOL" ? "Ticket No." : "Receipt No."}:</span> <b>{sys.first_no ? `${sys.first_no} – ${sys.last_no}` : "-"}</b></p>
      </div>

      <p className="font-heading font-bold text-maroon-900 mb-1">Shift-wise collection summary</p>
      <table className="w-full border border-slate-300 mb-4" data-testid="closing-modes">
        <thead><tr className="bg-maroon-800 text-white text-xs uppercase tracking-wider"><Th>Payment mode</Th><Th right>{module === "PROTOCOL" ? "Tickets" : "Sales"}</Th><Th right>System amount</Th><Th right>Actual</Th><Th right>Short / Excess</Th></tr></thead>
        <tbody>
          {PAY_MODES.map(({ key, label }) => {
            const actual = key === "CASH" ? v.cash_counted - v.opening_balance : v.actual[key];
            return (
              <tr key={key} className="border-t border-slate-200" data-testid={`closing-mode-${key.toLowerCase()}`}>
                <Td bold>{label}</Td><Td right>{sys.modes[key].count}</Td><Td right>{inr(sys.modes[key].amount)}</Td>
                <Td right>{inr(actual)}</Td><Td right bold className={diffClass(v.difference[key])}>{signed(v.difference[key])}</Td>
              </tr>
            );
          })}
          <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold">
            <Td>Total ({sys.count} {module === "PROTOCOL" ? "tickets" : "sales"}{sys.free ? `, ${sys.free} free` : ""})</Td><Td right>{sys.count - sys.free}</Td>
            <Td right>{inr(sys.total)}</Td><Td right>{inr(sys.total + v.net_difference)}</Td><Td right className={diffClass(v.net_difference)}>{signed(v.net_difference)}</Td>
          </tr>
        </tbody>
      </table>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="font-heading font-bold text-maroon-900 mb-1">Opening and closing balance (cash)</p>
          <table className="w-full border border-slate-300" data-testid="closing-balance">
            <tbody>
              <tr className="border-t border-slate-200"><Td>Opening balance</Td><Td right>{inr(v.opening_balance)}</Td></tr>
              <tr className="border-t border-slate-200"><Td>(+) Cash collected in shift</Td><Td right>{inr(sys.modes.CASH.amount)}</Td></tr>
              <tr className="border-t border-slate-200 bg-slate-50"><Td bold>Expected cash in hand</Td><Td right bold>{inr(v.expected_cash)}</Td></tr>
              <tr className="border-t border-slate-200"><Td>Cash counted</Td><Td right>{inr(v.cash_counted)}</Td></tr>
              <tr className="border-t border-slate-200"><Td bold>Cash short / excess</Td><Td right bold className={diffClass(v.difference.CASH)}>{signed(v.difference.CASH)}</Td></tr>
              <tr className="border-t border-slate-200"><Td>(−) Cash remitted to cashier / bank</Td><Td right>{inr(v.remitted)}</Td></tr>
              <tr className="border-t-2 border-maroon-800 bg-gold-50"><Td bold>Closing balance (carried forward)</Td><Td right bold>{inr(v.closing_balance)}</Td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <p className="font-heading font-bold text-maroon-900 mb-1">Cash denominations</p>
          <table className="w-full border border-slate-300" data-testid="closing-notes">
            <thead><tr className="bg-[#F7F3EE] text-xs text-slate-600"><Th>Note / coin</Th><Th right>Count</Th><Th right>Amount</Th></tr></thead>
            <tbody>
              {NOTES.filter((n) => Number(v.denominations?.[n])).map((n) => (
                <tr key={n} className="border-t border-slate-200"><Td>₹{n}</Td><Td right>{v.denominations[n]}</Td><Td right>{inr(n * v.denominations[n])}</Td></tr>
              ))}
              {!NOTES.some((n) => Number(v.denominations?.[n])) && <tr><td colSpan={3} className="py-3 text-center text-slate-400">Total entered without note count</td></tr>}
              <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold"><Td>Total cash</Td><Td /><Td right>{inr(v.cash_counted)}</Td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="font-heading font-bold text-maroon-900 mb-1">Operator-wise collection</p>
      <table className="w-full border border-slate-300 mb-4" data-testid="closing-operators">
        <thead><tr className="bg-maroon-800 text-white text-xs uppercase tracking-wider"><Th>Operator</Th><Th right>{module === "PROTOCOL" ? "Tickets" : "Sales"}</Th>{PAY_MODES.map((m) => <Th key={m.key} right>{m.label}</Th>)}<Th right>Total</Th></tr></thead>
        <tbody>
          {sys.operators.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">No collection in this shift</td></tr>}
          {sys.operators.map((o, i) => (
            <tr key={i} className="border-t border-slate-200"><Td bold>{o.operator}</Td><Td right>{o.count}{o.free ? ` (${o.free} free)` : ""}</Td>{PAY_MODES.map((m) => <Td key={m.key} right>{inr(o[m.key])}</Td>)}<Td right bold>{inr(o.total)}</Td></tr>
          ))}
        </tbody>
      </table>

      <p className="font-heading font-bold text-maroon-900 mb-1">{module === "PROTOCOL" ? "Darshan type-wise" : "Sale type-wise"}</p>
      <table className="w-full border border-slate-300 mb-3" data-testid="closing-items">
        <thead><tr className="bg-[#F7F3EE] text-xs text-slate-600"><Th>{module === "PROTOCOL" ? "Darshan type" : "Channel · saree type"}</Th><Th right>{module === "PROTOCOL" ? "Tickets" : "Sales"}</Th>{module === "PROTOCOL" && <Th right>Devotees</Th>}<Th right>Amount</Th></tr></thead>
        <tbody>
          {sys.items.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-slate-400">-</td></tr>}
          {sys.items.map((it, i) => (
            <tr key={i} className="border-t border-slate-200"><Td>{it.item}</Td><Td right>{it.count}</Td>{module === "PROTOCOL" && <Td right>{it.qty}</Td>}<Td right>{inr(it.amount)}</Td></tr>
          ))}
        </tbody>
      </table>

      <div className={`rounded-md border px-3 py-2 flex flex-wrap items-center justify-between gap-2 ${v.net_difference < 0 ? "border-red-300 bg-red-50" : v.net_difference > 0 ? "border-green-300 bg-green-50" : "border-slate-300 bg-slate-50"}`} data-testid="closing-result">
        <span className="font-bold">Result: {resultLabel(v.net_difference)}{v.net_difference ? ` ${inr(Math.abs(v.net_difference))}` : " (cash and settlement tally)"}</span>
        {v.remarks && <span className="text-slate-700">Remarks: {v.remarks}</span>}
      </div>
      {c && <p className="text-xs text-slate-500 mt-2">Closed by {c.closed_by} on {new Date(c.closed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}{c.edited ? ` · edited ${c.edited}×` : ""}</p>}
      <div className="flex justify-between mt-10 text-xs text-slate-600">
        <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Counter Operator</p></div>
        <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Cashier</p></div>
        <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Superintendent</p></div>
      </div>
    </div>
  );
}

function CloseShift({ module, meta, pick, onSaved }) {
  const { user } = useAuth();
  const admin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const canClose = user.role !== "SUPERVISOR";
  const ownShift = isCounterUser(user) && user.shift_id ? user.shift_id : ""; // counter staff: only their assigned shift
  const [f, setF] = useState({ temple_id: "", date: istDayKey(), shift_id: ownShift, counter_id: isCounterUser(user) ? user.counter_id || "" : "" });
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  const tid = f.temple_id || user.temple_id || "";
  const shifts = templeOptions(meta.shifts.filter((x) => x.status !== "INACTIVE"), tid, meta.temples);
  const counters = templeOptions(meta.counters, tid, meta.temples);
  const temple = meta.temples.find((t) => t.temple_id === (s?.temple_id || tid));

  // Super Admin: start on the first temple; everyone: start on the shift running now.
  useEffect(() => {
    if (user.role === "SUPER_ADMIN" && !f.temple_id && meta.temples.length) setF((x) => ({ ...x, temple_id: user.temple_id || meta.temples[0].temple_id }));
  }, [meta.temples]); // eslint-disable-line
  useEffect(() => {
    if (!f.shift_id && shifts.length) setF((x) => ({ ...x, shift_id: (shiftNow(shifts) || shifts[0]).id }));
  }, [shifts.length, tid]); // eslint-disable-line
  useEffect(() => { if (pick) setF((x) => ({ ...x, ...pick, ...(ownShift ? { shift_id: ownShift } : {}) })); }, [pick]); // eslint-disable-line

  const load = () => {
    if (!f.shift_id || !f.date) return;
    const q = new URLSearchParams({ module, date: f.date, shift_id: f.shift_id, counter_id: f.counter_id || "", temple_id: f.temple_id || "" });
    api.get(`/shift-closing/summary?${q}`).then((r) => {
      setS(r.data); setEditing(false);
      const c = r.data.closing, sys = r.data.system;
      setForm({
        opening_balance: String(c ? c.opening_balance : r.data.suggested_opening || 0),
        notes: Object.fromEntries(NOTES.map((n) => [n, c?.denominations?.[n] ? String(c.denominations[n]) : ""])),
        cash_total: c && !Object.keys(c.denominations || {}).length ? String(c.cash_counted) : "",
        upi_actual: String(c ? c.actual.UPI : sys.modes.UPI.amount),
        card_actual: String(c ? c.actual.CARD : sys.modes.CARD.amount),
        other_actual: String(c ? c.actual.OTHER : sys.modes.OTHER.amount),
        remitted: String(c ? c.remitted : ""), remarks: c?.remarks || "",
      });
    }).catch((e) => { setS(null); toast.error(fmtErr(e)); });
  };
  useEffect(load, [f.date, f.shift_id, f.counter_id, f.temple_id]); // eslint-disable-line

  // Live figures while the operator types (the server works them out again when saving).
  const live = useMemo(() => {
    if (!s || !form) return null;
    const sys = s.system;
    const notesTotal = NOTES.reduce((a, n) => a + n * (parseInt(form.notes[n], 10) || 0), 0);
    const usesNotes = NOTES.some((n) => parseInt(form.notes[n], 10) > 0);
    const counted = r2(usesNotes ? notesTotal : form.cash_total);
    const opening = r2(form.opening_balance);
    const actual = { CASH: counted, UPI: r2(form.upi_actual), CARD: r2(form.card_actual), OTHER: r2(form.other_actual) };
    const expected = r2(opening + sys.modes.CASH.amount);
    const difference = { CASH: r2(counted - expected), UPI: r2(actual.UPI - sys.modes.UPI.amount), CARD: r2(actual.CARD - sys.modes.CARD.amount), OTHER: r2(actual.OTHER - sys.modes.OTHER.amount) };
    const net = r2(difference.CASH + difference.UPI + difference.CARD + difference.OTHER);
    const remitted = r2(form.remitted);
    return {
      opening_balance: opening, cash_counted: counted, usesNotes, actual, expected_cash: expected, difference, net_difference: net, remitted,
      closing_balance: r2(counted - remitted), remarks: form.remarks,
      denominations: Object.fromEntries(NOTES.filter((n) => parseInt(form.notes[n], 10) > 0).map((n) => [n, parseInt(form.notes[n], 10)])),
    };
  }, [s, form]);

  const save = async () => {
    if (live.net_difference && !form.remarks.trim()) { toast.error("Enter a remark for the shortage / excess"); return; }
    if (live.remitted > live.cash_counted) { toast.error("Cash remitted cannot be more than the cash counted"); return; }
    setSaving(true);
    try {
      await api.post("/shift-closing", {
        module, date: f.date, shift_id: f.shift_id, counter_id: f.counter_id || "", temple_id: f.temple_id || "",
        opening_balance: live.opening_balance, denominations: live.denominations, cash_counted: live.cash_counted,
        upi_actual: live.actual.UPI, card_actual: live.actual.CARD, other_actual: live.actual.OTHER,
        remitted: live.remitted, remarks: form.remarks,
      });
      toast.success("Shift closed");
      load(); onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };
  const reopen = async () => {
    if (!window.confirm("Reopen this shift? The saved closing will be removed so it can be closed again.")) return;
    try { await api.delete(`/shift-closing/${s.closing.id}`); toast.success("Shift reopened"); load(); onSaved && onSaved(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const print = () => printRef.current && printHtml(printRef.current.outerHTML, "a4-page", `Shift_Closing_${module}_${f.date}_${s.shift.name}`);

  const setNote = (n, val) => setForm((x) => ({ ...x, notes: { ...x.notes, [n]: val.replace(/\D/g, "").slice(0, 6) } }));
  const setNum = (k) => (e) => setForm((x) => ({ ...x, [k]: e.target.value.replace(/[^\d.]/g, "") }));
  const closed = !!s?.closing && !editing;
  const own = meta.counters.find((c) => c.id === user.counter_id);

  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple</Label>
              <Select value={f.temple_id} onValueChange={(v) => setF({ ...f, temple_id: v, shift_id: "", counter_id: "" })}>
                <SelectTrigger data-testid="close-temple"><SelectValue placeholder="Temple" /></SelectTrigger>
                <SelectContent>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><Label className="text-xs">Date</Label><Input type="date" value={f.date} max={istDayKey()} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="close-date" /></div>
          <div><Label className="text-xs">Shift</Label>
            {ownShift ? (
              <p className="h-10 flex items-center px-3 text-sm rounded-md border border-[#E5D9C5] bg-[#FBF7EF] text-maroon-900" data-testid="close-own-shift">
                {user.shift_name || "My shift"}{user.shift_start ? ` (${user.shift_start}–${user.shift_end})` : ""}</p>
            ) : (
            <Select value={f.shift_id} onValueChange={(v) => setF({ ...f, shift_id: v })}>
              <SelectTrigger data-testid="close-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>{shifts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.start}–{x.end}){x.suffix}</SelectItem>)}</SelectContent>
            </Select>
            )}</div>
          <div><Label className="text-xs">Counter</Label>
            {isCounterUser(user) ? (
              <p className="h-10 flex items-center px-3 text-sm rounded-md border border-[#E5D9C5] bg-[#FBF7EF] text-maroon-900" data-testid="close-own-counter">{own ? own.name : "My collection"}</p>
            ) : (
              <Select value={f.counter_id || "ALL"} onValueChange={(v) => setF({ ...f, counter_id: v === "ALL" ? "" : v })}>
                <SelectTrigger data-testid="close-counter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All counters</SelectItem>{counters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.suffix}</SelectItem>)}</SelectContent>
              </Select>
            )}</div>
          <Button variant="outline" onClick={load} data-testid="close-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </CardContent>
      </Card>

      {s && form && live && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="close-tiles">
            {PAY_MODES.map(({ key, label, icon: Icon }) => (
              <div key={key} className={`rounded-xl px-3 py-3 ${MODE_TONE[key]}`} data-testid={`close-tile-${key.toLowerCase()}`}>
                <p className="text-xs font-semibold flex items-center gap-1.5"><Icon className="w-4 h-4" /> {label}</p>
                <p className="text-xl font-extrabold mt-1">{inr(s.system.modes[key].amount)}</p>
                <p className="text-[11px] opacity-80">{s.system.modes[key].count} {module === "PROTOCOL" ? "tickets" : "sales"}</p>
              </div>
            ))}
            <div className="rounded-xl px-3 py-3 bg-gradient-to-r from-[#A50F1F] to-[#E7561C] text-white col-span-2 sm:col-span-1" data-testid="close-tile-total">
              <p className="text-xs font-semibold flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Shift total</p>
              <p className="text-xl font-extrabold mt-1">{inr(s.system.total)}</p>
              <p className="text-[11px] opacity-90">{s.system.count} {module === "PROTOCOL" ? "tickets" : "sales"}{s.system.free ? ` · ${s.system.free} free` : ""}</p>
            </div>
          </div>

          {closed ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800" data-testid="close-closed-banner">
                <Lock className="w-4 h-4" /> This shift is closed by <b>{s.closing.closed_by}</b>.
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={print} data-testid="close-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                  {admin && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="close-edit"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>}
                  {admin && <Button size="sm" variant="outline" className="text-red-700" onClick={reopen} data-testid="close-reopen"><RotateCcw className="w-4 h-4 mr-1" /> Reopen</Button>}
                </div>
              </div>
              <Statement module={module} temple={temple} s={s} c={s.closing} innerRef={printRef} />
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4 items-start">
              <Statement module={module} temple={temple} s={s} live={live} innerRef={printRef} />
              <Card className="card-gold-top shadow-sm xl:sticky xl:top-4" data-testid="close-form">
                <CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-maroon-900">Close this shift</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Opening balance (cash float at start)</Label>
                    <Input value={form.opening_balance} onChange={setNum("opening_balance")} inputMode="decimal" data-testid="close-opening" />
                    {s.previous && <p className="text-[11px] text-slate-500 mt-0.5">Carried forward from {fmtDay(s.previous.date)} {s.previous.shift_name}: {inr(s.previous.closing_balance)}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Cash counted — note-wise</Label>
                    <div className="grid grid-cols-3 gap-1.5 mt-1">
                      {NOTES.map((n) => (
                        <div key={n} className="flex items-center rounded-md border border-[#E5D9C5] bg-white overflow-hidden">
                          <span className="w-12 shrink-0 text-center text-xs font-bold text-maroon-800 bg-gold-50 self-stretch flex items-center justify-center">₹{n}</span>
                          <input value={form.notes[n]} onChange={(e) => setNote(n, e.target.value)} inputMode="numeric" placeholder="0"
                            className="w-full min-w-0 px-2 h-9 text-sm text-right outline-none" data-testid={`close-note-${n}`} />
                        </div>
                      ))}
                    </div>
                    {!live.usesNotes && (
                      <div className="mt-2"><Label className="text-[11px] text-slate-500">or enter the total cash</Label>
                        <Input value={form.cash_total} onChange={setNum("cash_total")} inputMode="decimal" placeholder="0.00" data-testid="close-cash-total" /></div>
                    )}
                    <p className="mt-1 text-sm flex justify-between"><span className="text-slate-500">Cash counted</span><b data-testid="close-counted">{inr(live.cash_counted)}</b></p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">UPI actual</Label><Input value={form.upi_actual} onChange={setNum("upi_actual")} inputMode="decimal" data-testid="close-upi" /></div>
                    <div><Label className="text-xs">Card actual</Label><Input value={form.card_actual} onChange={setNum("card_actual")} inputMode="decimal" data-testid="close-card" /></div>
                    <div><Label className="text-xs">Other actual</Label><Input value={form.other_actual} onChange={setNum("other_actual")} inputMode="decimal" data-testid="close-other" /></div>
                  </div>
                  <div><Label className="text-xs">Cash remitted to cashier / bank</Label><Input value={form.remitted} onChange={setNum("remitted")} inputMode="decimal" placeholder="0.00" data-testid="close-remitted" /></div>
                  <div className="rounded-md bg-[#FBF7EF] border border-[#E5D9C5] p-3 text-sm space-y-1">
                    <p className="flex justify-between"><span>Expected cash</span><b>{inr(live.expected_cash)}</b></p>
                    <p className="flex justify-between"><span>Cash short / excess</span><b className={diffClass(live.difference.CASH)}>{signed(live.difference.CASH)}</b></p>
                    <p className="flex justify-between"><span>UPI / Card / Other</span><b className={diffClass(live.difference.UPI + live.difference.CARD + live.difference.OTHER)}>{signed(r2(live.difference.UPI + live.difference.CARD + live.difference.OTHER))}</b></p>
                    <p className="flex justify-between"><span>Closing balance</span><b>{inr(live.closing_balance)}</b></p>
                  </div>
                  <div className={`rounded-md px-3 py-2 flex items-center gap-2 font-bold ${live.net_difference < 0 ? "bg-red-100 text-red-800" : live.net_difference > 0 ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`} data-testid="close-net">
                    {live.net_difference ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    {resultLabel(live.net_difference)} {live.net_difference ? inr(Math.abs(live.net_difference)) : ""}
                  </div>
                  <div><Label className="text-xs">Remarks {live.net_difference ? <span className="text-red-600">* (required for shortage / excess)</span> : ""}</Label>
                    <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} data-testid="close-remarks" /></div>
                  {canClose ? (
                    <div className="flex gap-2">
                      {editing && <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
                      <Button onClick={save} disabled={saving} className="flex-1 h-11 bg-maroon-700 hover:bg-maroon-800 text-white font-heading tracking-wider" data-testid="close-save">
                        <Lock className="w-4 h-4 mr-2" /> {editing ? "SAVE CHANGES" : "CLOSE SHIFT"}
                      </Button>
                    </div>
                  ) : <p className="text-xs text-slate-500">Supervisors can view the shift figures; the counter / Temple Admin closes the shift.</p>}
                  <Button variant="outline" className="w-full" onClick={print} data-testid="close-print-draft"><Printer className="w-4 h-4 mr-1" /> Print draft</Button>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Register({ module, meta, refreshKey, onOpen }) {
  const { user } = useAuth();
  const today = istDayKey();
  const [f, setF] = useState({ frm: today.slice(0, 8) + "01", to: today, shift_id: "", counter_id: "", temple_id: "" });
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const shifts = templeOptions(meta.shifts, f.temple_id, meta.temples);
  const counters = templeOptions(meta.counters, f.temple_id, meta.temples);
  const temple = meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id));
  const run = () => {
    const q = new URLSearchParams({ module });
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/shift-closing?${q}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(run, [refreshKey]); // eslint-disable-line
  const set = (k) => (v) => setF({ ...f, [k]: v === "ALL" ? "" : v });
  const title = `Shift-wise DCR Closing Register — ${TITLES[module]} (${fmtDay(f.frm)} to ${fmtDay(f.to)})`;
  const pdf = () => {
    if (!data?.rows.length) { toast.error("No data to export"); return; }
    const t = data.totals, rs = (v) => `Rs ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    makePdf({
      temple, title: title.replace("—", "-"), landscape: true, fontSize: 7, filename: `Shift_Closing_${module}_${f.frm}_${f.to}.pdf`,
      head: ["Date", "Shift", "Counter", "Closed by", "Count", "Cash", "UPI", "Card", "Other", "Total", "Opening", "Counted", "Remitted", "Short/Excess", "Closing"],
      body: data.rows.map((r) => [fmtDay(r.date), r.shift_name, r.counter_name, r.closed_by, r.system.count, rs(r.system.modes.CASH.amount), rs(r.system.modes.UPI.amount), rs(r.system.modes.CARD.amount), rs(r.system.modes.OTHER.amount), rs(r.system.total), rs(r.opening_balance), rs(r.cash_counted), rs(r.remitted), `${r.result} ${rs(Math.abs(r.net_difference))}`, rs(r.closing_balance)]),
      foot: ["", "", "", "Total", t.count, rs(t.CASH), rs(t.UPI), rs(t.CARD), rs(t.OTHER), rs(t.total), "", rs(t.counted), rs(t.remitted), `Short ${rs(-t.shortage)} / Excess ${rs(t.excess)}`, ""],
    });
  };
  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="reg-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="reg-to" /></div>
          {user.role === "SUPER_ADMIN" && (
            <div><Label className="text-xs">Temple</Label>
              <Select value={f.temple_id || "ALL"} onValueChange={(v) => setF({ ...f, temple_id: v === "ALL" ? "" : v, shift_id: "", counter_id: "" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All temples</SelectItem>{meta.temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          {!(isCounterUser(user) && user.shift_id) && <div><Label className="text-xs">Shift</Label>
            <Select value={f.shift_id || "ALL"} onValueChange={set("shift_id")}>
              <SelectTrigger data-testid="reg-shift"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All shifts</SelectItem>{shifts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.start}–{x.end}){x.suffix}</SelectItem>)}</SelectContent>
            </Select></div>}
          {!isCounterUser(user) && (
            <div><Label className="text-xs">Counter</Label>
              <Select value={f.counter_id || "ALL"} onValueChange={set("counter_id")}>
                <SelectTrigger data-testid="reg-counter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All counters</SelectItem>{counters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.suffix}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="flex gap-2 lg:col-span-2">
            <Button onClick={run} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="reg-run">View Report</Button>
            <Button onClick={pdf} variant="outline" title="Download PDF" data-testid="reg-pdf"><FileDown className="w-4 h-4" /></Button>
            <Button onClick={() => ref.current && printHtml(ref.current.outerHTML, "a4-page", `Shift_Closing_Register_${f.frm}_${f.to}`)} variant="outline" title="Print" data-testid="reg-print"><Printer className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
      {data && (
        <div ref={ref} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-5 overflow-x-auto" data-testid="reg-report">
          <ReportHeader title={title} temple={temple} />
          <table className="w-full text-xs border border-slate-300">
            <thead><tr className="bg-maroon-800 text-white uppercase tracking-wider text-[10px]">
              <Th>Date</Th><Th>Shift</Th><Th>Counter</Th><Th>Closed by</Th><Th right>Count</Th>
              {PAY_MODES.map((m) => <Th key={m.key} right>{m.label}</Th>)}<Th right>Total</Th><Th right>Opening</Th><Th right>Counted</Th><Th right>Short / Excess</Th><Th right>Closing</Th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={14} className="py-6 text-center text-slate-400">No shift closings in this period</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-200 hover:bg-gold-50 cursor-pointer" onClick={() => onOpen(r)} title="Open this closing" data-testid={`reg-row-${r.id}`}>
                  <Td>{fmtDay(r.date)}</Td><Td>{r.shift_name}</Td><Td>{r.counter_name}</Td><Td>{r.closed_by}</Td><Td right>{r.system.count}</Td>
                  {PAY_MODES.map((m) => <Td key={m.key} right>{inr(r.system.modes[m.key].amount)}</Td>)}
                  <Td right bold>{inr(r.system.total)}</Td><Td right>{inr(r.opening_balance)}</Td><Td right>{inr(r.cash_counted)}</Td>
                  <Td right bold className={diffClass(r.net_difference)}>{signed(r.net_difference)}</Td><Td right>{inr(r.closing_balance)}</Td>
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="reg-totals">
                  <Td>Total</Td><Td /><Td /><Td>{data.rows.length} closings</Td><Td right>{data.totals.count}</Td>
                  {PAY_MODES.map((m) => <Td key={m.key} right>{inr(data.totals[m.key])}</Td>)}
                  <Td right>{inr(data.totals.total)}</Td><Td /><Td right>{inr(data.totals.counted)}</Td>
                  <Td right><span className="text-red-700">{signed(data.totals.shortage)}</span><br /><span className="text-green-700">{signed(data.totals.excess)}</span></Td><Td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ShiftClosing({ module }) {
  const meta = useCounterShiftMeta();
  const [tab, setTab] = useState("close");
  const [pick, setPick] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const open = (r) => { setPick({ temple_id: r.temple_id, date: r.date, shift_id: r.shift_id, counter_id: r.counter_id || "" }); setTab("close"); };
  return (
    <div data-testid="shift-closing-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900">Shift-wise DCR Closing</h1>
      <p className="text-sm text-slate-500 mb-4">{TITLES[module]} — collection by payment mode and operator, opening / closing balance and shortage / excess for each shift.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5D9C5]">
          <TabsTrigger value="close" data-testid="tab-close">Close Shift</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Closing Register</TabsTrigger>
        </TabsList>
        <TabsContent value="close" className="mt-4" forceMount hidden={tab !== "close"}><CloseShift module={module} meta={meta} pick={pick} onSaved={() => setRefreshKey((k) => k + 1)} /></TabsContent>
        <TabsContent value="register" className="mt-4"><Register module={module} meta={meta} refreshKey={refreshKey} onOpen={open} /></TabsContent>
      </Tabs>
    </div>
  );
}
